import { useEffect, useRef, useState } from 'react'
import {
  useLikeSocialOrderMutation,
  useSubscribeSocialOrderMutation,
  useUnlikeSocialOrderMutation,
  useUnsubscribeSocialOrderMutation,
} from '../api/socialOrders'
import type { ISocialOrderResource } from '../api/types/socialOrders'
import { profileApi } from '../api/profile'
import { useSettledToggleNetwork } from './useSettledToggleNetwork'

type SocialOrderEngagementSource = Pick<
  ISocialOrderResource,
  'id' | 'author_id' | 'slug' | 'viewer' | 'is_followed' | 'counters'
>

type UseSocialOrderEngagementOptions = {
  socialOrder: SocialOrderEngagementSource
  cacheKey?: number | string
}

export function useSocialOrderEngagement({ socialOrder, cacheKey }: UseSocialOrderEngagementOptions) {
  const { data: me } = profileApi.useGetProfileQuery()
  const [isSubscribed, setIsSubscribed] = useState(Boolean(socialOrder.viewer?.is_followed ?? socialOrder.is_followed))
  const [subscribersCount, setSubscribersCount] = useState(Number(socialOrder.counters.subscribers ?? 0))
  const [isLiked, setIsLiked] = useState(Boolean(socialOrder.viewer?.is_liked))
  const [likesCount, setLikesCount] = useState(Number(socialOrder.counters.likes ?? 0))
  const [subscribeSocialOrder, { isLoading: isSubscribing }] = useSubscribeSocialOrderMutation()
  const [unsubscribeSocialOrder, { isLoading: isUnsubscribing }] = useUnsubscribeSocialOrderMutation()
  const [likeSocialOrder, { isLoading: isLiking }] = useLikeSocialOrderMutation()
  const [unlikeSocialOrder, { isLoading: isUnliking }] = useUnlikeSocialOrderMutation()
  const subscribeSettle = useSettledToggleNetwork()
  const likeSettle = useSettledToggleNetwork()
  const subscribeBurstRef = useRef<{ value: boolean; count: number } | null>(null)
  const likeBurstRef = useRef<{ value: boolean; count: number } | null>(null)

  useEffect(() => {
    setIsSubscribed(Boolean(socialOrder.viewer?.is_followed ?? socialOrder.is_followed))
    setSubscribersCount(Number(socialOrder.counters.subscribers ?? 0))
    setIsLiked(Boolean(socialOrder.viewer?.is_liked))
    setLikesCount(Number(socialOrder.counters.likes ?? 0))
  }, [socialOrder])

  const toggleSubscribe = () => {
    if (!subscribeBurstRef.current) {
      subscribeBurstRef.current = { value: isSubscribed, count: subscribersCount }
    }
    const next = !isSubscribed
    setIsSubscribed(next)
    setSubscribersCount((n) => Math.max(0, n + (isSubscribed ? -1 : 1)))

    subscribeSettle.schedule({
      desired: next,
      commit: async (desired) => {
        subscribeBurstRef.current = null
        if (desired) {
          await subscribeSocialOrder({ socialOrderId: socialOrder.id, cacheKey }).unwrap()
        } else {
          await unsubscribeSocialOrder({ socialOrderId: socialOrder.id, cacheKey }).unwrap()
        }
      },
      onSkip: () => {
        subscribeBurstRef.current = null
      },
      onError: () => {
        const burst = subscribeBurstRef.current
        subscribeBurstRef.current = null
        if (burst) {
          setIsSubscribed(burst.value)
          setSubscribersCount(burst.count)
        }
      },
    })
  }

  const toggleLike = () => {
    if (!likeBurstRef.current) {
      likeBurstRef.current = { value: isLiked, count: likesCount }
    }
    const next = !isLiked
    setIsLiked(next)
    setLikesCount((n) => Math.max(0, n + (isLiked ? -1 : 1)))

    likeSettle.schedule({
      desired: next,
      commit: async (desired) => {
        likeBurstRef.current = null
        if (desired) {
          await likeSocialOrder({ socialOrderId: socialOrder.id, cacheKey }).unwrap()
        } else {
          await unlikeSocialOrder({ socialOrderId: socialOrder.id, cacheKey }).unwrap()
        }
      },
      onSkip: () => {
        likeBurstRef.current = null
      },
      onError: () => {
        const burst = likeBurstRef.current
        likeBurstRef.current = null
        if (burst) {
          setIsLiked(burst.value)
          setLikesCount(burst.count)
        }
      },
    })
  }

  return {
    isAuthor: Boolean(socialOrder.viewer?.permissions?.is_owner) || me?.id === socialOrder.author_id,
    isSubscribed,
    subscribersCount,
    isLiked,
    likesCount,
    toggleSubscribe,
    toggleLike,
    isSubscribeLoading: isSubscribing || isUnsubscribing,
    isLikeLoading: isLiking || isUnliking,
  }
}
